import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";

import "./Flyout.scss";

const getAnimationPropsForDirection = (direction) => {
  switch (direction) {
    case "left": {
      return {
        initial: { x: "-125%", opacity: 1 },
        variants: {
          closed: {
            x: "-125%",
            opacity: 1,
          },
          active: {
            x: 0,
            opacity: 1,
          },
          minimized: {
            x: "-85%",
            opacity: 0.8,
          },
        },
      };
    }
    case "bottom": {
      return {
        initial: { y: "125%", opacity: 1 },
        variants: {
          closed: {
            y: "125%",
            opacity: 1,
          },
          active: {
            y: 0,
            opacity: 1,
          },
          minimized: {
            y: "70%",
            opacity: 0.8,
          },
        },
      };
    }
  }
};

const AngleLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
    <path
      fill="currentColor"
      d="M192 448c-8.188 0-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25l160-160c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l137.4 137.4c12.5 12.5 12.5 32.75 0 45.25C208.4 444.9 200.2 448 192 448z"
    />
  </svg>
);

const AngleRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
    <path
      fill="currentColor"
      d="M64 448c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L178.8 256L41.38 118.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l160 160c12.5 12.5 12.5 32.75 0 45.25l-160 160C80.38 444.9 72.19 448 64 448z"
    />
  </svg>
);

const AngleDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
    <path
      fill="currentColor"
      d="M192 384c-8.188 0-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L192 306.8l137.4-137.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-160 160C208.4 380.9 200.2 384 192 384z"
    />
  </svg>
);

const AngleUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
    <path
      fill="currentColor"
      d="M352 352c-8.188 0-16.38-3.125-22.62-9.375L192 205.3l-137.4 137.4c-12.5 12.5-32.75 12.5-45.25 0s-12.5-32.75 0-45.25l160-160c12.5-12.5 32.75-12.5 45.25 0l160 160c12.5 12.5 12.5 32.75 0 45.25C368.4 348.9 360.2 352 352 352z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Flyout = ({
  children,
  canBeMinimized = true,
  canBeClosed = false,
  direction = "left",
  onClose,
}) => {
  const [animate, setAnimate] = useState("closed");
  const isMinimized = animate === "minimized";

  useEffect(() => {
    setAnimate("active");
  }, []);

  const toggleMinimized = () => {
    setAnimate(isMinimized ? "active" : "minimized");
  };

  // TODO: render to portal
  return (
    <motion.div
      {...getAnimationPropsForDirection(direction)}
      transition={{ type: "spring", duration: 0.5, bounce: 0 }}
      animate={animate}
      className={classNames("flyout", `flyout--${direction}`)}
    >
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        style={{ height: "100%" }}
      >
        {canBeMinimized && (
          <button className="flyout__minimize-button" onClick={toggleMinimized}>
            {direction === "bottom" && (
              <React.Fragment>
                {isMinimized && <AngleUp />}
                {!isMinimized && <AngleDown />}
              </React.Fragment>
            )}
            {direction === "left" && (
              <React.Fragment>
                {isMinimized && <AngleRight />}
                {!isMinimized && <AngleLeft />}
              </React.Fragment>
            )}
          </button>
        )}
        {canBeClosed && (
          <button className="flyout__minimize-button" onClick={onClose}>
            <CloseIcon />
          </button>
        )}
        {children}
      </motion.div>
    </motion.div>
  );
};

export default Flyout;
